import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import RoleGuard from '@/components/auth/RoleGuard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import type { RouterOutputs } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { toast } from '@/hooks/use-toast';
import { usePageTitle } from '@/contexts/PageTitleContext';

type ShipmentStatus = 'pending' | 'in_transit' | 'delivered' | 'cancelled';
type Forwarder = RouterOutputs['shipments']['listForwarders'][number];
type PurchaseOrder = RouterOutputs['purchaseOrders']['getOrders']['data'][number];

const STATUS_OPTIONS: Array<{ value: ShipmentStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const TRANSPORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'sea_freight', label: 'Sea freight' },
  { value: 'air_freight', label: 'Air freight' },
  { value: 'courier', label: 'Courier' },
];

const normalizeCurrency = (value: string) => value.trim().toUpperCase();

export default function ShipmentCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const utils = trpc.useUtils();
  const { selectedLocation, locations } = useLocation();
  const { organization } = useOrganization();
  const { format: formatMoney } = useCurrencyFormatter();

  const orgCurrency = normalizeCurrency(organization?.default_currency ?? 'USD');
  const prefilledLocationId = searchParams.get('locationId');
  const prefilledPurchaseOrderIds = useMemo(() => {
    const idsFromSingle = searchParams.get('purchaseOrderId');
    const idsFromList = searchParams.get('purchaseOrderIds');
    const parsedList = idsFromList
      ? idsFromList.split(',').map((value) => value.trim()).filter(Boolean)
      : [];
    const combined = [...(idsFromSingle ? [idsFromSingle] : []), ...parsedList];
    return Array.from(new Set(combined));
  }, [searchParams]);

  const [locationId, setLocationId] = useState<string>(prefilledLocationId ?? selectedLocation?.id ?? '');
  const [freightForwarderId, setFreightForwarderId] = useState<string | null>(null);
  const [status, setStatus] = useState<ShipmentStatus>('pending');
  const [shippedDate, setShippedDate] = useState<string>('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState<string>('');
  const [intoPortDate, setIntoPortDate] = useState<string>('');
  const [atWarehouseDate, setAtWarehouseDate] = useState<string>('');
  // Literal union matches the backend router enum + DB CHECK ('' = unset).
  const [modeOfTransport, setModeOfTransport] = useState<
    'sea_freight' | 'air_freight' | 'courier' | ''
  >('');
  const [totalUnits, setTotalUnits] = useState<string>('');
  const [totalCartons, setTotalCartons] = useState<string>('');
  const [weight, setWeight] = useState<string>('');
  const [cbm, setCbm] = useState<string>('');
  const [trackingLink, setTrackingLink] = useState<string>('');
  const [freightCost, setFreightCost] = useState<string>('');
  const [currency, setCurrency] = useState<string>('AUD');
  const [exchangeRate, setExchangeRate] = useState<string>('1');
  const [notes, setNotes] = useState<string>('');

  const [selectedPoIds, setSelectedPoIds] = useState<string[]>([]);
  const [poPickerOpen, setPoPickerOpen] = useState(false);
  const [didApplyLocationPrefill, setDidApplyLocationPrefill] = useState(false);
  const [didApplyPoPrefill, setDidApplyPoPrefill] = useState(false);

  const [forwarderDialogOpen, setForwarderDialogOpen] = useState(false);
  const [newForwarder, setNewForwarder] = useState({
    name: '',
    email: '',
    phone: '',
    default_currency: 'AUD',
  });

  useEffect(() => {
    if (selectedLocation?.id && !locationId) {
      setLocationId(selectedLocation.id);
    }
  }, [selectedLocation?.id, locationId]);

  useEffect(() => {
    if (didApplyLocationPrefill) return;
    if (prefilledLocationId?.trim()) {
      setLocationId(prefilledLocationId.trim());
    }
    setDidApplyLocationPrefill(true);
  }, [didApplyLocationPrefill, prefilledLocationId]);

  const forwardersQuery = trpc.shipments.listForwarders.useQuery(
    { includeInactive: false },
    { staleTime: 60_000 }
  );

  const purchaseOrdersQuery = trpc.purchaseOrders.getOrders.useQuery(
    { locationId: locationId || undefined },
    { enabled: Boolean(locationId), staleTime: 30_000 }
  );

  const createShipment = trpc.shipments.createShipment.useMutation({
    onSuccess: async (shipment) => {
      await utils.shipments.listShipments.invalidate();
      toast({ title: 'Shipment created', description: shipment.shipment_number ?? 'Shipment created' });
      navigate(`/shipments/${shipment.id}`);
    },
    onError: (err) => toast({ title: 'Failed to create shipment', description: err.message, variant: 'destructive' }),
  });

  const createForwarder = trpc.shipments.createForwarder.useMutation({
    onSuccess: async (forwarder) => {
      await utils.shipments.listForwarders.invalidate();
      setFreightForwarderId(forwarder.id);
      setCurrency(normalizeCurrency(forwarder.default_currency ?? currency));
      setForwarderDialogOpen(false);
      setNewForwarder({ name: '', email: '', phone: '', default_currency: 'AUD' });
      toast({ title: 'Forwarder created' });
    },
    onError: (err) => toast({ title: 'Failed to create forwarder', description: err.message, variant: 'destructive' }),
  });

  const forwarders: Forwarder[] = forwardersQuery.data ?? [];

  const orders: PurchaseOrder[] = useMemo(() => {
    const rows = purchaseOrdersQuery.data?.data ?? [];
    return rows
      .filter((po) => po.status !== 'cancelled')
      .filter((po) => po.status !== 'draft'); // draft POs are usually not relevant for shipments
  }, [purchaseOrdersQuery.data?.data]);

  useEffect(() => {
    if (didApplyPoPrefill) return;
    if (prefilledPurchaseOrderIds.length === 0) {
      setDidApplyPoPrefill(true);
      return;
    }
    if (purchaseOrdersQuery.isLoading) {
      return;
    }

    const orderIdSet = new Set(orders.map((order) => order.id));
    const matchedIds = prefilledPurchaseOrderIds.filter((id) => orderIdSet.has(id));
    if (matchedIds.length > 0) {
      setSelectedPoIds((prev) => Array.from(new Set([...prev, ...matchedIds])));
    }
    setDidApplyPoPrefill(true);
  }, [
    didApplyPoPrefill,
    prefilledPurchaseOrderIds,
    purchaseOrdersQuery.isLoading,
    orders,
  ]);

  const selectedOrders = useMemo(() => {
    const map = new Map(orders.map((po) => [po.id, po]));
    return selectedPoIds.map((id) => map.get(id)).filter(Boolean) as PurchaseOrder[];
  }, [orders, selectedPoIds]);

  const availableOrders = useMemo(() => {
    const selected = new Set(selectedPoIds);
    return orders.filter((po) => !selected.has(po.id));
  }, [orders, selectedPoIds]);

  const handleForwarderChange = (value: string) => {
    if (value === '__none__') {
      setFreightForwarderId(null);
      return;
    }
    if (value === '__new__') {
      setForwarderDialogOpen(true);
      return;
    }
    setFreightForwarderId(value);
    const selected = forwarders.find((f) => f.id === value);
    const defaultCurrency = selected?.default_currency ?? null;
    if (defaultCurrency) {
      setCurrency(normalizeCurrency(defaultCurrency));
      if (normalizeCurrency(defaultCurrency) === orgCurrency) {
        setExchangeRate('1');
      }
    }
  };

  const handleCreate = () => {
    if (!locationId) {
      toast({ title: 'Location is required', variant: 'destructive' });
      return;
    }

    const payload = {
      locationId,
      freightForwarderId,
      status,
      shippedDate: shippedDate.trim() ? shippedDate.trim() : null,
      expectedDeliveryDate: expectedDeliveryDate.trim() ? expectedDeliveryDate.trim() : null,
      // Cast-gated new logistics fields (migration 20260706060000). Extra keys
      // pass through structurally; the parallel backend schema accepts them.
      intoPortDate: intoPortDate.trim() ? intoPortDate.trim() : null,
      atWarehouseDate: atWarehouseDate.trim() ? atWarehouseDate.trim() : null,
      modeOfTransport: modeOfTransport ? modeOfTransport : null,
      totalUnits: totalUnits.trim() ? Number(totalUnits) : null,
      totalCartons: totalCartons.trim() ? Number(totalCartons) : null,
      weight: weight.trim() ? Number(weight) : null,
      cbm: cbm.trim() ? Number(cbm) : null,
      trackingLink: trackingLink.trim() ? trackingLink.trim() : null,
      freightCost: freightCost.trim() ? Number(freightCost) : null,
      currency: currency.trim() ? normalizeCurrency(currency) : null,
      exchangeRate: exchangeRate.trim() ? Number(exchangeRate) : null,
      notes: notes.trim() ? notes.trim() : null,
      purchaseOrderIds: selectedPoIds.length > 0 ? selectedPoIds : null,
    };

    createShipment.mutate(payload);
  };

  const previewFreight = () => {
    if (!freightCost.trim()) return '-';
    const value = Number(freightCost);
    if (!Number.isFinite(value)) return '-';
    return formatMoney(value, currency || null);
  };

  usePageTitle(
    'New Shipment',
    'Shipments are per-location. Choose a location first, then link purchase orders.',
    { label: 'Back to Shipments', to: '/shipments' },
  );

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="flex-1 container py-6 max-w-5xl space-y-6">
          {/* "Back to Shipments" lives in the TopHeader (typewriter button). */}
          <Card>
            <CardHeader>
              <CardTitle>Create Shipment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Accordion
                type="multiple"
                defaultValue={['basics', 'schedule', 'purchase-orders']}
                className="w-full"
              >
                <AccordionItem value="basics">
                  <AccordionTrigger className="text-base font-medium">Basics</AccordionTrigger>
                  <AccordionContent className="space-y-6 pt-2">
                    <div className="grid gap-2">
                      <Label>Location</Label>
                      <Select value={locationId || '__none__'} onValueChange={(value) => {
                        setLocationId(value === '__none__' ? '' : value);
                        setSelectedPoIds([]);
                      }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Select location…</SelectItem>
                          {locations.map((loc) => (
                            <SelectItem key={loc.id} value={loc.id}>
                              {loc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-2">
                      <Label>Status</Label>
                      <Select value={status} onValueChange={(value) => setStatus(value as ShipmentStatus)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-2">
                      <Label>Freight Forwarder</Label>
                      <Select
                        value={freightForwarderId ?? '__none__'}
                        onValueChange={handleForwarderChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select forwarder" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No forwarder</SelectItem>
                          {forwarders.map((f) => (
                            <SelectItem key={f.id} value={f.id}>
                              {f.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="__new__" className="text-primary">
                            + Add new forwarder
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="schedule">
                  <AccordionTrigger className="text-base font-medium">Schedule & Logistics</AccordionTrigger>
                  <AccordionContent className="space-y-6 pt-2">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Ship Date</Label>
                        <Input
                          type="date"
                          value={shippedDate}
                          onChange={(e) => setShippedDate(e.target.value)}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>ETA</Label>
                        <Input
                          type="date"
                          value={expectedDeliveryDate}
                          onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Into port date</Label>
                        <Input
                          type="date"
                          value={intoPortDate}
                          onChange={(e) => setIntoPortDate(e.target.value)}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>At warehouse date</Label>
                        <Input
                          type="date"
                          value={atWarehouseDate}
                          onChange={(e) => setAtWarehouseDate(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Mode of transport</Label>
                        <Select
                          value={modeOfTransport || '__none__'}
                          onValueChange={(value) =>
                            setModeOfTransport(
                              value === '__none__'
                                ? ''
                                : (value as 'sea_freight' | 'air_freight' | 'courier'),
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select transport" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Not set</SelectItem>
                            {TRANSPORT_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                          <Label>Total units</Label>
                          <Input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            value={totalUnits}
                            onChange={(e) => setTotalUnits(e.target.value)}
                            placeholder="0"
                            className="tabular-nums"
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label>Total cartons</Label>
                          <Input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            value={totalCartons}
                            onChange={(e) => setTotalCartons(e.target.value)}
                            placeholder="0"
                            className="tabular-nums"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Weight (kg)</Label>
                        <Input
                          inputMode="decimal"
                          value={weight}
                          onChange={(e) => setWeight(e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>CBM</Label>
                        <Input
                          inputMode="decimal"
                          value={cbm}
                          onChange={(e) => setCbm(e.target.value)}
                          placeholder="0.000"
                        />
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label>Tracking Link</Label>
                      <Input
                        value={trackingLink}
                        onChange={(e) => setTrackingLink(e.target.value)}
                        placeholder="URL or tracking number"
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="costs">
                  <AccordionTrigger className="text-base font-medium">Costs & Notes</AccordionTrigger>
                  <AccordionContent className="space-y-6 pt-2">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Currency</Label>
                        <Select value={currency} onValueChange={(value) => {
                          setCurrency(value);
                          if (normalizeCurrency(value) === orgCurrency) setExchangeRate('1');
                        }}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AUD">AUD</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="CNY">CNY</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label>Exchange Rate</Label>
                        <Input
                          inputMode="decimal"
                          value={exchangeRate}
                          onChange={(e) => setExchangeRate(e.target.value)}
                          placeholder="1.0"
                          disabled={normalizeCurrency(currency) === 'AUD'}
                        />
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <Label>Freight Cost ({currency})</Label>
                        <div className="text-xs text-muted-foreground">Preview: {previewFreight()}</div>
                      </div>
                      <Input
                        inputMode="decimal"
                        value={freightCost}
                        onChange={(e) => setFreightCost(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Optional notes..."
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="purchase-orders">
                  <AccordionTrigger className="text-base font-medium">
                    <div className="flex items-center gap-2">
                      <span>Linked Purchase Orders</span>
                      <Badge variant="secondary">{selectedOrders.length}</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-2 pt-2">
                    <div className="flex items-center justify-end">
                      <Popover open={poPickerOpen} onOpenChange={setPoPickerOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" disabled={!locationId}>
                            <Plus className="mr-2 h-4 w-4" />
                            Link PO
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[420px] p-0" align="end">
                          <Command>
                            <CommandInput placeholder="Search purchase orders..." />
                            <CommandList>
                              <CommandEmpty>
                                {purchaseOrdersQuery.isLoading ? 'Loading...' : 'No purchase orders found.'}
                              </CommandEmpty>
                              <CommandGroup>
                                {availableOrders.map((po) => (
                                  <CommandItem
                                    key={po.id}
                                    onSelect={() => {
                                      setSelectedPoIds((prev) => [...prev, po.id]);
                                      setPoPickerOpen(false);
                                    }}
                                    className="flex items-center justify-between"
                                  >
                                    <span className="font-medium">
                                      {po.po_number ?? '-'}{' '}
                                      <span className="text-muted-foreground font-normal">
                                        {po.title ? `· ${po.title}` : ''}
                                      </span>
                                    </span>
                                    <Badge variant="muted">{(po.status ?? '').replace('_', ' ')}</Badge>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {selectedOrders.length === 0 ? (
                      <div className="rounded-md border p-4 text-sm text-muted-foreground">
                        No purchase orders linked yet.
                      </div>
                    ) : (
                      <div className="rounded-md border divide-y">
                        {selectedOrders.map((po) => (
                          <div key={po.id} className="flex items-center justify-between p-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{po.po_number ?? '-'}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {po.title ?? '-'} {po.supplier_name ? `· ${po.supplier_name}` : ''}{' '}
                                {po.total_cost != null ? `· ${formatMoney(po.total_cost, po.currency ?? null)}` : ''}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedPoIds((prev) => prev.filter((id) => id !== po.id))}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {/* Form footer actions (§10 forms keep their own footer actions). */}
          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/shipments')}
              disabled={createShipment.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createShipment.isPending}>
              Create Shipment
            </Button>
          </div>
        </main>
      </div>

      {/* Create forwarder inline */}
      <Dialog open={forwarderDialogOpen} onOpenChange={setForwarderDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Add Freight Forwarder</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Name</Label>
              <Input
                value={newForwarder.name}
                onChange={(e) => setNewForwarder((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Forwarder name"
              />
            </div>
            <div className="grid gap-2">
              <Label>Email (comma-separated)</Label>
              <Input
                value={newForwarder.email}
                onChange={(e) => setNewForwarder((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="ops@example.com, accounts@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label>Phone</Label>
              <Input
                value={newForwarder.phone}
                onChange={(e) => setNewForwarder((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="+61 ..."
              />
            </div>
            <div className="grid gap-2">
              <Label>Default Currency</Label>
              <Select
                value={newForwarder.default_currency}
                onValueChange={(value) => setNewForwarder((prev) => ({ ...prev, default_currency: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AUD">AUD</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="CNY">CNY</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setForwarderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const name = newForwarder.name.trim();
                if (!name) {
                  toast({ title: 'Name is required', variant: 'destructive' });
                  return;
                }
                createForwarder.mutate({
                  name,
                  email: newForwarder.email.trim() ? newForwarder.email.trim() : null,
                  phone: newForwarder.phone.trim() ? newForwarder.phone.trim() : null,
                  default_currency: newForwarder.default_currency,
                  is_active: true,
                });
              }}
              disabled={createForwarder.isPending}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </RoleGuard>
  );
}
